/* ============================================
   Prospección — la lista outbound

   Los prospectos son leads en etapa `prospecto`: el
   mismo registro que después vive en el pipeline, pero
   trabajado de otra forma. Por eso acá hay una tabla
   con filtros y paginación, y no un tablero: son
   cientos de filas que se recorren en tanda, no diez
   oportunidades que se arrastran entre columnas.

   Cuando se contacta a uno, pasa a `contactado` y
   desaparece de esta lista: ya es del tablero.
   ============================================ */
import { useState, useMemo, useCallback } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData, useInvalidar } from '@/hooks/useCachedData'
import { showToast } from '@/components/layout/Toast'
import type { Lead, PrioridadProspecto } from '@/lib/types'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import { descargarCsv, type CsvColumn } from '@/lib/csv'
import LeadDetailView from '@/components/crm/LeadDetailView'
import ImportarProspectos from '@/components/crm/ImportarProspectos'
import EmptyState from '@/components/shared/EmptyState'
import {
  IconSearch, IconDownload, IconTarget, IconExternalLink, IconGlobe,
  IconMail, IconArrowRight, IconUser, IconUpload,
} from '@/components/icons/Icons'

const CRUMBS = [{ label: 'CRM' }, { label: 'Prospección' }]
const PRIORIDADES: PrioridadProspecto[] = ['A', 'B', 'C']
const TODAS = 'todas'

/** Ordena por prioridad (A primero) y, dentro, por empresa. */
function porPrioridad(a: Lead, b: Lead): number {
  const peso = (p: string | null) => (p === 'A' ? 0 : p === 'B' ? 1 : p === 'C' ? 2 : 3)
  const dif = peso(a.prioridad) - peso(b.prioridad)
  if (dif !== 0) return dif
  return (a.empresa || a.codigo).localeCompare(b.empresa || b.codigo, 'es')
}

export default function Prospeccion() {
  const api = useApi()
  const [seleccionado, setSeleccionado] = useState<Lead | null>(null)
  const [promoviendo, setPromoviendo] = useState<number | null>(null)
  const [importando, setImportando] = useState(false)

  const [search, setSearch] = useState('')
  const [filtroPrioridad, setFiltroPrioridad] = useState(TODAS)
  const [filtroIndustria, setFiltroIndustria] = useState(TODAS)
  const [filtroCiudad, setFiltroCiudad] = useState(TODAS)

  const apiGet = api.get
  const invalidar = useInvalidar()

  // Clave propia y no `['leads']`: esta lista es la de `etapa=prospecto`, que
  // el Pipeline justamente excluye. Compartirla dejaria una de las dos
  // pantallas mostrando la lista de la otra.
  const { data: prospectos = [], isLoading: loading, refrescar, actualizar } = useCachedData(
    ['leads', 'prospecto'],
    () => apiGet<Lead[]>('/api/leads?etapa=prospecto').catch(err => {
      console.error('Error cargando prospectos:', err)
      return [] as Lead[]
    }),
  )

  const cargar = useCallback(async () => {
    const r = await refrescar()
    // La ficha abierta se refresca con la fila nueva, sin cerrarse.
    const data = r.data
    if (data) setSeleccionado(prev => (prev ? data.find(l => l.id === prev.id) ?? null : null))
  }, [refrescar])

  const industrias = useMemo(
    () => [...new Set(prospectos.map(p => p.industria).filter(Boolean))].sort() as string[],
    [prospectos],
  )
  const ciudades = useMemo(
    () => [...new Set(prospectos.map(p => p.ciudad).filter(Boolean))].sort() as string[],
    [prospectos],
  )

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    return prospectos
      .filter(p => {
        if (filtroPrioridad !== TODAS && p.prioridad !== filtroPrioridad) return false
        if (filtroIndustria !== TODAS && p.industria !== filtroIndustria) return false
        if (filtroCiudad !== TODAS && p.ciudad !== filtroCiudad) return false
        if (!q) return true
        return [p.empresa, p.codigo, p.ciudad, p.industria, p.senal_dolor, p.servicio_interes]
          .some(campo => campo?.toLowerCase().includes(q))
      })
      .sort(porPrioridad)
  }, [prospectos, search, filtroPrioridad, filtroIndustria, filtroCiudad])

  const firmaFiltros = `${search}|${filtroPrioridad}|${filtroIndustria}|${filtroCiudad}`
  const { page, setPage, totalPages, paginated, pageSize, total } = usePagination(filtrados, 15, firmaFiltros)

  const resumen = useMemo(() => ({
    total: prospectos.length,
    a: prospectos.filter(p => p.prioridad === 'A').length,
    b: prospectos.filter(p => p.prioridad === 'B').length,
    sinContacto: prospectos.filter(p => !p.contactos || p.contactos.length === 0).length,
  }), [prospectos])

  const hayFiltros = search.trim() !== '' || filtroPrioridad !== TODAS
    || filtroIndustria !== TODAS || filtroCiudad !== TODAS

  function limpiarFiltros() {
    setSearch(''); setFiltroPrioridad(TODAS); setFiltroIndustria(TODAS); setFiltroCiudad(TODAS)
  }

  /**
   * Contactado = sale de la lista y entra al tablero. La fecha de primer
   * contacto se sella acá: hasta ahora estaba en NULL porque nadie lo había
   * tocado, y es la que alimenta el contador de días del pipeline.
   */
  async function pasarAContactado(lead: Lead) {
    setPromoviendo(lead.id)
    try {
      await api.put(`/api/leads/${lead.id}`, {
        etapa: 'contactado',
        fecha_primer_contacto: new Date().toISOString().slice(0, 10),
      })
      actualizar(prev => prev.filter(p => p.id !== lead.id))
      // Ya no es prospecto: el Pipeline tiene que verlo cuando se entre.
      invalidar(['leads'])
      if (seleccionado?.id === lead.id) setSeleccionado(null)
      showToast(`${lead.empresa || lead.codigo} pasó al pipeline`, 'success')
    } catch (err: any) {
      console.error('Error promoviendo prospecto:', err)
      showToast(err?.message || 'No se pudo mover el prospecto', 'error')
    } finally {
      setPromoviendo(null)
    }
  }

  function exportarCsv() {
    const columnas: CsvColumn<Lead>[] = [
      ['Código', p => p.codigo],
      ['Empresa', p => p.empresa],
      ['Ciudad', p => p.ciudad],
      ['Industria', p => p.industria],
      ['Prioridad', p => p.prioridad],
      ['Señal de dolor', p => p.senal_dolor],
      ['Servicio a ofrecer', p => p.servicio_interes],
      ['LinkedIn empresa', p => p.linkedin_url],
      ['Web', p => p.web_url],
      ['Contacto', p => p.contactos?.[0] ? `${p.contactos[0].nombre} ${p.contactos[0].apellido || ''}`.trim() : ''],
      ['Email contacto', p => p.contactos?.[0]?.email],
      ['LinkedIn contacto', p => p.contactos?.[0]?.linkedin],
    ]
    descargarCsv(`prospeccion-${new Date().toISOString().slice(0, 10)}`, columnas, filtrados)
  }

  // La ficha se abre sobre la pantalla, igual que en el pipeline.
  if (seleccionado) {
    return (
      <LeadDetailView
        lead={seleccionado}
        onClose={() => setSeleccionado(null)}
        onRefresh={() => cargar()}
        origen="Prospección"
      />
    )
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando prospectos...</p></div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="prospeccion">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === prospectos.length
          ? `${prospectos.length} prospecto${prospectos.length !== 1 ? 's' : ''}`
          : `${total} de ${prospectos.length}`}
        actions={
          <>
            {prospectos.length > 0 && (
              <button className="btn btn-secondary" onClick={exportarCsv} title="Descarga en CSV lo que está filtrado">
                <IconDownload size={15} /> Exportar
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setImportando(true)}>
              <IconUpload size={15} /> Importar
            </button>
          </>
        }
      />

      <ImportarProspectos
        open={importando}
        onClose={() => setImportando(false)}
        onImportado={() => cargar()}
      />

      {prospectos.length > 0 && (
        <KpiStrip>
          <KpiRow>
            <StatCard label="Prospectos" value={resumen.total} />
            <StatCard label="Prioridad A" value={resumen.a} tone="danger" />
            <StatCard label="Prioridad B" value={resumen.b} tone="warning" />
            <StatCard label="Sin contacto" value={resumen.sinContacto}
              tone={resumen.sinContacto > 0 ? 'warning' : 'success'} />
          </KpiRow>
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            className="form-input"
            placeholder="Buscar por empresa, ciudad, industria o señal..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-input" value={filtroPrioridad} onChange={e => setFiltroPrioridad(e.target.value)} style={{ width: 150 }}>
          <option value={TODAS}>Toda prioridad</option>
          {PRIORIDADES.map(p => <option key={p} value={p}>Prioridad {p}</option>)}
        </select>
        <select className="form-input" value={filtroIndustria} onChange={e => setFiltroIndustria(e.target.value)} style={{ width: 200 }}>
          <option value={TODAS}>Toda industria</option>
          {industrias.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select className="form-input" value={filtroCiudad} onChange={e => setFiltroCiudad(e.target.value)} style={{ width: 180 }}>
          <option value={TODAS}>Toda ciudad</option>
          {ciudades.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {prospectos.length === 0 ? (
        <EmptyState
          icon={<IconTarget size={48} />}
          title="Sin prospectos"
          description="Acá vive la lista outbound: las empresas que estás buscando y con las que todavía no hablaste. Importá la planilla del SDR para empezar."
          actionLabel="Importar planilla"
          onAction={() => setImportando(true)}
        />
      ) : filtrados.length === 0 ? (
        <div className="empty-state">
          <h3>Ningún prospecto coincide</h3>
          <p>Probá con otros filtros.</p>
          <button className="btn btn-secondary" onClick={limpiarFiltros}>Limpiar filtros</button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Ciudad</th>
                  <th>Industria</th>
                  <th>Prio.</th>
                  <th>Servicio a ofrecer</th>
                  <th>Contacto</th>
                  <th>Enlaces</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginated.map(p => {
                  const contacto = p.contactos?.find(c => c.es_principal) || p.contactos?.[0]
                  return (
                    <tr key={p.id} className="row--clickable" onClick={() => setSeleccionado(p)}>
                      <td>
                        <div className="cell-stack">
                          <span className="cell-stack__title">{p.empresa || p.codigo}</span>
                          <span className="cell-stack__sub">{p.codigo}</span>
                        </div>
                      </td>
                      <td>{p.ciudad || '—'}</td>
                      <td>{p.industria || '—'}</td>
                      <td>
                        {p.prioridad
                          ? <span className={`prio-chip prio-chip--${p.prioridad.toLowerCase()}`}>{p.prioridad}</span>
                          : '—'}
                      </td>
                      <td className="prospeccion__servicio">{p.servicio_interes || '—'}</td>
                      <td>
                        {contacto ? (
                          <div className="cell-stack">
                            <span className="cell-stack__title">
                              <IconUser size={11} /> {contacto.nombre} {contacto.apellido || ''}
                            </span>
                            {contacto.cargo && <span className="cell-stack__sub">{contacto.cargo}</span>}
                          </div>
                        ) : (
                          <span className="text-muted-italic">Sin contacto</span>
                        )}
                      </td>
                      <td>
                        <div className="prospeccion__enlaces" onClick={e => e.stopPropagation()}>
                          {p.linkedin_url && (
                            <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" title="LinkedIn de la empresa">
                              <IconExternalLink size={14} />
                            </a>
                          )}
                          {p.web_url && (
                            <a href={p.web_url} target="_blank" rel="noopener noreferrer" title="Sitio web">
                              <IconGlobe size={14} />
                            </a>
                          )}
                          {contacto?.email && (
                            <a href={`mailto:${contacto.email}`} title={contacto.email}>
                              <IconMail size={14} />
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={e => { e.stopPropagation(); pasarAContactado(p) }}
                          disabled={promoviendo === p.id}
                          title="Ya le escribiste: pasa al pipeline"
                        >
                          {promoviendo === p.id ? '...' : <>Contactado <IconArrowRight size={13} /></>}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
            onPage={setPage} noun="prospectos" />
        </>
      )}
    </div>
  )
}
